import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Check, Flame, Camera, Phone } from 'lucide-react'
import { pricingPackages, pricingAddons, siteData } from '../../data/siteData'

export default function Pricing() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="pricing" className="relative py-20 sm:py-28 pricing-section">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-tactical-accent/30 to-transparent" />

      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-tactical-text tracking-wider leading-none">
            CENNIK ASG & PAINTBALL
          </h2>
          <p className="font-worksans text-base text-tactical-text/70 mt-5 max-w-xl mx-auto leading-relaxed">
            Dostosowujemy się do Ciebie. Sprawdź pakiety i wybierz ten, który pasuje do Twojej ekipy.
          </p>
        </motion.div>

        {/* PAKIETY — 2-kolumnowy grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {pricingPackages.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className={`relative rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-[3px] pricing-card ${
                plan.popular
                  ? 'pricing-card-popular border-2 border-tactical-accent/60'
                  : 'border border-tactical-border'
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1 bg-tactical-accent text-tactical-bg font-mono text-[10px] font-bold uppercase tracking-wider rounded-full">
                    <Check size={10} strokeWidth={3} />
                    Polecany
                  </span>
                </div>
              )}

              {/* Glow gradient for popular card */}
              {plan.popular && (
                <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-tactical-accent/60 to-transparent rounded-full" />
              )}

              {/* Header */}
              <div className="mb-5">
                <div className="flex items-center gap-3 mb-1">
                  <p className="font-worksans text-xs text-tactical-text/50 uppercase tracking-wider">
                    {plan.range}
                  </p>
                  <span className="w-1 h-1 rounded-full bg-tactical-text/30" />
                  <p className="font-worksans text-xs text-tactical-text/50 uppercase tracking-wider">
                    {plan.duration}
                  </p>
                </div>
                <h3 className="font-display text-2xl text-tactical-text tracking-wider">
                  {plan.name}
                </h3>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-display text-6xl sm:text-7xl text-tactical-text tracking-wide leading-none">
                  {plan.price}
                </span>
                <span className="font-worksans text-sm text-tactical-text/60">
                  {plan.unit}
                </span>
              </div>

              {/* Features header */}
              <p className="font-worksans text-[10px] text-tactical-text/40 uppercase tracking-[0.2em] mb-3">
                Zestaw zawiera
              </p>

              {/* Features */}
              <ul className="space-y-2.5 mb-8">
                {plan.features.map((feature, fi) => (
                  <li key={fi} className="flex items-start gap-3">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 18 18"
                      fill="none"
                      className="flex-shrink-0 mt-0.5"
                    >
                      <path
                        d="M3.5 9.5L7 13L14.5 5"
                        stroke="#a8cc00"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="font-worksans text-sm text-tactical-text/80 leading-relaxed">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Ammo block */}
              <div className="rounded-xl bg-black/20 px-5 py-4 border border-tactical-border/40">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="font-worksans text-xs text-tactical-text/50">
                    {plan.ammo.label}
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-xl text-tactical-accent tracking-wide">
                      {plan.ammo.price}
                    </span>
                    <span className="font-worksans text-xs text-tactical-text/50">
                      {plan.ammo.detail}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* DODATKI — 2-kolumnowy grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-16"
        >
          <h3 className="font-display text-2xl text-tactical-text tracking-wider text-center mb-8">
            DODATKI DO PAKIETU
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {pricingAddons.map((addon) => (
              <div
                key={addon.name}
                className="flex items-start gap-5 rounded-2xl border border-tactical-border p-5 sm:p-6 pricing-card transition-all duration-300 hover:-translate-y-[3px]"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-tactical-accent/10 border border-tactical-accent/20 flex items-center justify-center text-tactical-accent">
                  {addon.icon === 'Flame' ? (
                    <Flame size={22} strokeWidth={1.5} />
                  ) : (
                    <Camera size={22} strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <h4 className="font-display text-lg text-tactical-text tracking-wider">
                        {addon.name}
                      </h4>
                      {'duration' in addon && addon.duration && (
                        <span className="font-worksans text-[10px] text-tactical-text/40 uppercase tracking-wider">
                          {addon.duration}
                        </span>
                      )}
                    </div>
                    <span className="font-display text-2xl text-tactical-accent tracking-wide">
                      {addon.price} <span className="text-sm text-tactical-text/50">{addon.unit}</span>
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {addon.features.map((f, idx) => (
                      <li key={idx} className="font-worksans text-xs text-tactical-text/60 leading-relaxed">
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Banner "Wycena indywidualna" */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative overflow-hidden rounded-2xl border border-tactical-border p-6 sm:p-8 mb-10 pricing-banner"
        >
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <h3 className="font-display text-xl sm:text-2xl text-tactical-text tracking-wider mb-1">
                WYCENA INDYWIDUALNA
              </h3>
              <p className="font-worksans text-sm text-tactical-text/60 max-w-lg">
                Przy braniu więcej niż jednego pakietu usług przygotujemy ofertę dopasowaną specjalnie dla Ciebie.
              </p>
            </div>
            <a
              href={`tel:${siteData.phone}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-tactical-accent text-tactical-bg font-worksans font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_20px_rgba(168,204,0,0.25)] hover:brightness-110 flex-shrink-0"
            >
              <Phone size={16} />
              Zadzwoń
            </a>
          </div>
        </motion.div>

        {/* Footer line inside section */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="text-center font-worksans text-xs text-tactical-text/40 tracking-wide"
        >
          Ceny brutto.
        </motion.p>
      </div>
    </section>
  )
}
